import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Input, Select, Textarea, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { STOCK_COUNT_STATUSES, StockCountClient, type StockCountDTO } from "../../api/stockCount";
import { NexpimView } from "../../views";
import { localToday } from "./StockAdjustmentForm";
import { StockCountStatusBadge, stockCountStatusLabel } from "./stockCountBadges";

/**
 * Count sheets, paged in the database.
 *
 * <p>Opens on the sheets still being counted. A sheet belongs to one warehouse
 * of the ledger; distribution stock is counted in Nexera and is not offered.</p>
 */
export function StockCountsPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const counts = useMemo(() => new StockCountClient(http), [http]);
  const [creating, setCreating] = useState(false);

  const table = useServerTable<StockCountDTO>({
    queryKey: ["manufacturing", "stock-count"],
    fetchPage: (request) => counts.find(request),
    fetchTotal: () => counts.count(),
    enabled: allowed,
  });

  const cols = useMemo(() => {
    const c = defineColumns<StockCountDTO>();
    return [
      c.number("number", (row) => row.number, { headerKey: "column.count.number", filter: "range", priority: 1 }),
      c.text("name", (row) => row.name, { headerKey: "column.count.name", priority: 1 }),
      c.custom("status", (row) => row.status, (row) => <StockCountStatusBadge status={row.status} />, {
        headerKey: "column.status",
        filter: "multiselect",
        filterOptions: STOCK_COUNT_STATUSES.map((value) => ({ value, label: stockCountStatusLabel(t, value) })),
        priority: 2,
      }),
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { headerKey: "column.stock.warehouse", filter: "none", priority: 2 }),
      c.text("count_date", (row) => row.count_date ?? "", { headerKey: "column.count.date", filter: "daterange", priority: 2 }),
      c.number("line_count", (row) => row.line_count, { headerKey: "column.count.lines", filter: "range", priority: 3 }),
      // Not a queryable field on the server: sorting by it was refused with a 400.
      c.number("counted_count", (row) => row.counted_count, {
        headerKey: "column.count.counted",
        filter: "none",
        enableSorting: false,
        priority: 3,
      }),
      c.number("variance_count", (row) => row.variance_count, { headerKey: "column.count.variances", filter: "range", priority: 3 }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("count.title.list", "Physical counts")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        parent={{ to: "/manufacturing/stock", label: t("navigation.item.stock", "Stock") }}
        title={t("count.title.list", "Physical counts")}
        subtitle={t("count.subtitle", "Count sheets: prepared, counted on the floor, posted to the ledger once.")}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t("count.new", "New count")}
            </Button>
          ) : null
        }
      />
      <DataTable<StockCountDTO>
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/manufacturing/stock/counts"
        loading={table.loading}
        server={table.mode}
        initialFilters={[{ id: "status", value: ["OPEN"] }]}
        onRowClick={(row) => void navigate({ to: "/manufacturing/stock/counts/$countId", params: { countId: String(row.id) } })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      <NewCountDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

/** A sheet needs a name, a warehouse and a day; the parts are added on the sheet itself. */
function NewCountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [date, setDate] = useState(localToday);
  const [note, setNote] = useState("");

  // Cancelling and reopening starts a fresh sheet, not the abandoned one.
  const close = () => {
    onClose();
    setName("");
    setWarehouseId("");
    setDate(localToday());
    setNote("");
  };

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: open,
    staleTime: 30 * 60_000,
  });
  const countable = (warehouses.data ?? []).filter((w) => w.is_active && w.kind !== "DISTRIBUTION");
  const effectiveWarehouse = warehouseId || String(countable.find((w) => w.kind === "MANUFACTURING")?.id ?? "");

  const create = useMutation({
    mutationFn: () =>
      new StockCountClient(http).create({
        id: 0,
        number: 0,
        status: "OPEN",
        warehouse_id: Number(effectiveWarehouse),
        name: name.trim(),
        count_date: date || null,
        note: note.trim() || null,
      }),
    onSuccess: async (count) => {
      close();
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-count"] });
      void navigate({ to: "/manufacturing/stock/counts/$countId", params: { countId: String(count.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const canCreate = name.trim() !== "" && effectiveWarehouse !== "";

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("count.new", "New count")}
      actions={
        <>
          <Button variant="ghost" onClick={close}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!canCreate}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t("count.create", "Create sheet")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("column.count.name", "Name")} htmlFor="count-new-name" required>
          <Input
            id="count-new-name"
            value={name}
            placeholder={t("count.name.placeholder", "Go-live count, cycle count, hardware aisle…")}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canCreate && !create.isPending) create.mutate();
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("form.field.warehouse", "Warehouse")} htmlFor="count-new-warehouse" required>
            <Select
              id="count-new-warehouse"
              value={effectiveWarehouse}
              options={countable.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
              onChange={(event) => setWarehouseId(event.target.value)}
            />
          </Field>
          <Field label={t("column.count.date", "Count date")} htmlFor="count-new-date">
            <Input id="count-new-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </Field>
        </div>
        <Field label={t("form.field.note", "Note")} htmlFor="count-new-note">
          <Textarea id="count-new-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        <p className="text-[12px] text-ink-muted">
          {t("count.new.hint", "Distribution warehouses are counted in Nexera and are not offered here. Add the parts to count on the sheet.")}
        </p>
      </div>
    </Dialog>
  );
}
